<script lang="ts">
	import { SimpleLicenseService } from '$lib/services/simpleLicenseService';
	import { authStore, register, setVerificationNeeded } from '$lib/stores/user';
	import { createEventDispatcher } from 'svelte';
	import { t } from '$lib/stores/i18n';
	import VerificationCard from './VerificationCard.svelte';

	export let isOpen: boolean = false;
	export let onClose = () => {};
	export let onRegisterSuccess = (user: any) => {};
	export let onClickLogin = () => {};

	const dispatch = createEventDispatcher();

	// Form data
	let formData = {
		name: '',
		email: '',
		password: '',
		password_confirmation: '',
		customer_type: 'personal', // 'personal' or 'company'
		first_name: '',
		last_name: '',
		mobile: '',
		address: '',
		company_name: '',
		tax_id: ''
	};

	let errorMessage = '';
	let validationErrors: { [key: string]: string[] } = {};
	let showVerification = false;
	let registeredEmail = '';
	let showPassword = false;
	let showPasswordConfirmation = false;

	// Subscribe to auth store for loading state
	$: isLoading = $authStore.isLoading;

	// Clear errors when user types
	$: if (Object.values(formData).some((value) => value !== '')) {
		errorMessage = '';
		validationErrors = {};
	}

	function handleCustomerTypeChange(type: string) {
		formData.customer_type = type;
		// Clear company fields when switching to personal
		if (type === 'personal') {
			formData.company_name = '';
			formData.tax_id = '';
		}
	}

	async function handleRegister() {
		// Basic validation
		if (!formData.email || !formData.password || !formData.password_confirmation) {
			errorMessage = $t('register.errors.emptyFields');
			return;
		}

		if (formData.password !== formData.password_confirmation) {
			errorMessage = $t('register.errors.passwordMismatch');
			return;
		}

		// Validate company fields if customer type is company
		if (formData.customer_type === 'company' && (!formData.company_name || !formData.tax_id)) {
			errorMessage = $t('register.errors.companyFieldsRequired');
			return;
		}

		errorMessage = '';
		validationErrors = {};

		try {
			// Get license information for device binding
			const licenseService = SimpleLicenseService.getInstance();
			await licenseService.initialize();
			const licenseInfo = licenseService.getLicenseForServerValidation();

			// Create registration request object
			const registrationData: any = {
				name: formData.first_name + ' ' + formData.last_name || formData.email.split('@')[0],
				email: formData.email,
				password: formData.password,
				password_confirmation: formData.password_confirmation,
				customer_type: formData.customer_type,
				first_name: formData.first_name,
				last_name: formData.last_name,
				mobile: formData.mobile,
				address: formData.address,
				...(formData.customer_type === 'company' && {
					company_name: formData.company_name,
					tax_id: formData.tax_id
				}),
				// 🔐 เพิ่ม license information สำหรับผูก device กับ user account
				license_info: {
					license_id: licenseInfo.licenseId,
					device_fingerprint: licenseInfo.deviceFingerprint,
					created_at: licenseInfo.createdAt,
					version: licenseInfo.version
				}
			};

			console.log('🔐 Registering with license info:', {
				email: formData.email,
				licenseId: licenseInfo.licenseId,
				deviceFingerprint: licenseInfo.deviceFingerprint?.substring(0, 16) + '...'
			});

			const response = await register(registrationData);

			// Check if user needs verification
			if (response.user && !response.user.email_verified_at) {
				// Set verification needed and show verification modal
				setVerificationNeeded(formData.email);
				registeredEmail = formData.email;
				showVerification = true;
			} else {
				// User doesn't need verification, proceed normally
				onRegisterSuccess(response.user);
				onClose();
			}

			dispatch('register-success', response.user);
		} catch (error: any) {
			// Handle different types of errors
			if (error.response?.data) {
				const errorData = error.response.data;

				if (errorData.errors) {
					// Validation errors
					validationErrors = errorData.errors;
				errorMessage = errorData.message || $t('register.errors.checkInformation');
			} else {
				// General error
				errorMessage = errorData.message || $t('register.errors.registrationFailed');
			}
		} else {
			errorMessage = $t('register.errors.connectionError');
			}
		}
	}

	function handleLogin() {
		onClickLogin();
	}

	function handleKeyPress(event: KeyboardEvent) {
		if (event.key === 'Enter') {
			handleRegister();
		}
	}

	// Verification handlers
	function handleVerificationSuccess() {
		showVerification = false;
		onRegisterSuccess({ email: registeredEmail });
		onClose();
	}

	function handleVerificationBack() {
		showVerification = false;
	}

	function handleVerificationClose() {
		showVerification = false;
		onClose();
	}

	function handleResendCode() {
		// Handle resend logic if needed
		console.log('Resending code to:', registeredEmail);
	}

	// Modal click behavior tracking
	let mouseDownInsideModal = false;

	function handleMouseDown(event: MouseEvent) {
		mouseDownInsideModal = true;
	}

	function handleOverlayClick(event: MouseEvent) {
		if (mouseDownInsideModal) {
			mouseDownInsideModal = false;
			return;
		}
		onClose();
	}
</script>

{#if isOpen && !showVerification}
	<div
		class="modal-overlay"
		on:click={handleOverlayClick}
		on:keydown={(e) => e.key === 'Escape' && onClose()}
		role="dialog"
		aria-modal="true"
		tabindex="-1"
	>
		<!-- svelte-ignore a11y-click-events-have-key-events -->
		<div
			class="modal-container"
			on:click|stopPropagation
			on:mousedown={handleMouseDown}
			role="button"
			tabindex="0"
		>
			<div class="modal-header">
				<button class="close-button" on:click={onClose} aria-label={$t('register.ariaLabels.close')}>
					<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
						<path d="M6 6L18 18M6 18L18 6" />
					</svg>
				</button>
			</div>

			<div class="modal-content">
				<!-- Shield Icon -->
				<div class="icon-container">
					<img src="/assets/img/icons/login_secue.png" alt="Shield Icon" />
				</div>

				<h2 class="modal-title">{$t('register.title')}</h2>

				<form class="registration-form" on:submit|preventDefault={handleRegister}>
					<!-- Basic Information -->
					<div class="form-row full-width">
						<input
							type="email"
							bind:value={formData.email}
							placeholder={$t('register.emailPlaceholder')}
							class="form-input {validationErrors.email ? 'error' : ''}"
							on:keypress={handleKeyPress}
							disabled={isLoading}
							required
						/>
						{#if validationErrors.email}
							<div class="field-error">
								{#each validationErrors.email as error}
									<p>{error}</p>
								{/each}
							</div>
						{/if}
					</div>

					<!-- Personal Information -->
					<div class="form-row">
						<div class="form-group">
							<input
								type="text"
								bind:value={formData.first_name}
								placeholder={$t('register.firstNamePlaceholder')}
								class="form-input {validationErrors.first_name ? 'error' : ''}"
								on:keypress={handleKeyPress}
								disabled={isLoading}
							/>
							{#if validationErrors.first_name}
								<div class="field-error">
									{#each validationErrors.first_name as error}
										<p>{error}</p>
									{/each}
								</div>
							{/if}
						</div>

						<div class="form-group">
							<input
								type="text"
								bind:value={formData.last_name}
								placeholder={$t('register.lastNamePlaceholder')}
								class="form-input {validationErrors.last_name ? 'error' : ''}"
								on:keypress={handleKeyPress}
								disabled={isLoading}
							/>
							{#if validationErrors.last_name}
								<div class="field-error">
									{#each validationErrors.last_name as error}
										<p>{error}</p>
									{/each}
								</div>
							{/if}
						</div>
					</div>

					<!-- Password -->
					<div class="form-row full-width">
						<div class="form-group">
							<div class="password-input-container">
								<input
									type={showPassword ? 'text' : 'password'}
									bind:value={formData.password}
									placeholder={$t('register.passwordPlaceholder')}
									class="form-input {validationErrors.password ? 'error' : ''}"
									on:keypress={handleKeyPress}
									disabled={isLoading}
									required
								/>
								<button
									type="button"
									class="password-toggle"
									on:click={() => (showPassword = !showPassword)}
				aria-label={showPassword ? $t('register.ariaLabels.hidePassword') : $t('register.ariaLabels.showPassword')}
								>
									{#if showPassword}
										<svg
											width="20"
											height="20"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
										>
											<path
												d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94L17.94 17.94z"
											/>
											<path d="M1 1l22 22" />
											<path
												d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19L9.9 4.24z"
											/>
										</svg>
									{:else}
										<svg
											width="20"
											height="20"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
										>
											<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
											<circle cx="12" cy="12" r="3" />
										</svg>
									{/if}
								</button>
							</div>
							{#if validationErrors.password}
								<div class="field-error">
									{#each validationErrors.password as error}
										<p>{error}</p>
									{/each}
								</div>
							{/if}
						</div>
					</div>

					<div class="form-row full-width">
						<div class="form-group">
							<div class="password-input-container">
								<input
									type={showPasswordConfirmation ? 'text' : 'password'}
									bind:value={formData.password_confirmation}
									placeholder={$t('register.confirmPasswordPlaceholder')}
									class="form-input {validationErrors.password_confirmation ? 'error' : ''}"
									on:keypress={handleKeyPress}
									disabled={isLoading}
									required
								/>
								<button
									type="button"
									class="password-toggle"
									on:click={() => (showPasswordConfirmation = !showPasswordConfirmation)}
				aria-label={showPasswordConfirmation
					? $t('register.ariaLabels.hidePasswordConfirmation')
					: $t('register.ariaLabels.showPasswordConfirmation')}
								>
									{#if showPasswordConfirmation}
										<svg
											width="20"
											height="20"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
										>
											<path
												d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94L17.94 17.94z"
											/>
											<path d="M1 1l22 22" />
											<path
												d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19L9.9 4.24z"
											/>
										</svg>
									{:else}
										<svg
											width="20"
											height="20"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
										>
											<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
											<circle cx="12" cy="12" r="3" />
										</svg>
									{/if}
								</button>
							</div>
							{#if validationErrors.password_confirmation}
								<div class="field-error">
									{#each validationErrors.password_confirmation as error}
										<p>{error}</p>
									{/each}
								</div>
							{/if}
						</div>
					</div>

					<!-- Contact Information -->
					<div class="form-group">
						<input
							type="tel"
							bind:value={formData.mobile}
							placeholder={$t('register.mobilePlaceholder')}
							class="form-input {validationErrors.mobile ? 'error' : ''}"
							on:keypress={handleKeyPress}
							disabled={isLoading}
						/>
						{#if validationErrors.mobile}
							<div class="field-error">
								{#each validationErrors.mobile as error}
									<p>{error}</p>
								{/each}
							</div>
						{/if}
					</div>

					<div class="form-group">
						<textarea
							bind:value={formData.address}
							placeholder={$t('register.addressPlaceholder')}
							class="form-input form-textarea {validationErrors.address ? 'error' : ''}"
							disabled={isLoading}
							rows="2"
						></textarea>
						{#if validationErrors.address}
							<div class="field-error">
								{#each validationErrors.address as error}
									<p>{error}</p>
								{/each}
							</div>
						{/if}
					</div>

					<!-- Customer Type Selection -->
					<div class="form-group">
						<div class="form-label">{$t('register.customerType')}</div>
						<div class="customer-type-group">
							<button
								type="button"
								class="customer-type-btn {formData.customer_type === 'personal' ? 'active' : ''}"
								on:click={() => handleCustomerTypeChange('personal')}
							>
								<i class="fas fa-user"></i>
								{$t('register.personal')}
							</button>
							<button
								type="button"
								class="customer-type-btn {formData.customer_type === 'company' ? 'active' : ''}"
								on:click={() => handleCustomerTypeChange('company')}
							>
								<i class="fas fa-building"></i>
								{$t('register.company')}
							</button>
						</div>
					</div>

					<!-- Company Information (only if customer_type is company) -->
					{#if formData.customer_type === 'company'}
						<div class="company-section">
							<div class="form-group">
								<input
									type="text"
									bind:value={formData.company_name}
									placeholder={$t('register.companyNamePlaceholder')}
									class="form-input {validationErrors.company_name ? 'error' : ''}"
									on:keypress={handleKeyPress}
									disabled={isLoading}
									required
								/>
								{#if validationErrors.company_name}
									<div class="field-error">
										{#each validationErrors.company_name as error}
											<p>{error}</p>
										{/each}
									</div>
								{/if}
							</div>

							<div class="form-group">
								<input
									type="text"
									bind:value={formData.tax_id}
									placeholder={$t('register.taxIdPlaceholder')}
									class="form-input {validationErrors.tax_id ? 'error' : ''}"
									on:keypress={handleKeyPress}
									disabled={isLoading}
									required
								/>
								{#if validationErrors.tax_id}
									<div class="field-error">
										{#each validationErrors.tax_id as error}
											<p>{error}</p>
										{/each}
									</div>
								{/if}
							</div>
						</div>
					{/if}

					{#if errorMessage}
						<div class="error-message">
							{errorMessage}
						</div>
					{/if}

					<button type="submit" class="register-button" disabled={isLoading}>
						{#if isLoading}
							<div class="spinner"></div>
							{$t('register.creatingAccount')}
						{:else}
							{$t('register.createAccountButton')}
						{/if}
					</button>
				</form>

				<div class="login-link">
					<span>{$t('register.alreadyHaveAccount')} </span>
					<button class="link-button" on:click={handleLogin}>{$t('register.login')}</button>
				</div>
			</div>
		</div>
	</div>
{/if}

<!-- Verification Card -->
<VerificationCard
	bind:isOpen={showVerification}
	email={registeredEmail}
	onClose={handleVerificationClose}
	onVerifySuccess={handleVerificationSuccess}
	onResendCode={handleResendCode}
	on:back={handleVerificationBack}
/>

<style scoped>
	.modal-overlay {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: rgba(0, 0, 0, 0.8);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 1000;
		padding: 20px;
	}

	.modal-container {
		background: #1a1a1a;
		border-radius: 16px;
		width: 100%;
		max-width: 600px;
		max-height: 90vh;
		overflow-y: auto;
		box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
	}

	.modal-header {
		display: flex;
		justify-content: flex-end;
		padding: 20px 32px 0;
	}

	.close-button {
		background: #333;
		border: none;
		color: #fff;
		width: 32px;
		height: 32px;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		transition: background-color 0.2s;
	}

	.close-button:hover {
		background: #555;
	}

	.modal-content {
		padding: 0 32px 32px 32px;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 24px;
	}

	.icon-container {
		margin-bottom: 8px;
	}

	.modal-title {
		color: #ffffff;
		font-size: 24px;
		font-weight: 600;
		margin: 0;
		text-align: center;
	}

	.registration-form {
		width: 100%;
		display: flex;
		flex-direction: column;
		gap: 16px;
	}

	.form-group {
		display: flex;
		flex-direction: column;
		gap: 4px;
	}

	.form-row {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 16px;
	}

	.form-row.full-width {
		grid-template-columns: 1fr;
	}

	@media (max-width: 768px) {
		.form-row {
			grid-template-columns: 1fr;
		}
	}

	.form-label {
		color: #ffffff;
		font-size: 14px;
		font-weight: 600;
		margin-bottom: 8px;
	}

	.customer-type-group {
		display: flex;
		gap: 8px;
	}

	.customer-type-btn {
		flex: 1;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
		padding: 12px 16px;
		background: #2a2a2a;
		border: 2px solid #404040;
		border-radius: 8px;
		color: #ffffff;
		cursor: pointer;
		transition: all 0.2s ease;
		font-size: 14px;
		font-weight: 500;
	}

	.customer-type-btn:hover {
		background: #3a3a3a;
		border-color: #555;
	}

	.customer-type-btn.active {
		background: #4a6cf7;
		border-color: #4a6cf7;
		color: #ffffff;
	}

	.customer-type-btn i {
		font-size: 16px;
	}

	.form-input {
		background: #2a2a2a;
		border: 1px solid #404040;
		border-radius: 8px;
		padding: 12px 16px;
		color: #ffffff;
		font-size: 14px;
		transition: border-color 0.2s;
	}

	.form-input::placeholder {
		color: #888;
	}

	.form-input:focus {
		outline: none;
		border-color: #4a6cf7;
	}

	.form-input:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}

	.form-input.error {
		border-color: #ff4444;
		background: rgba(255, 68, 68, 0.05);
	}

	.password-input-container {
		position: relative;
	}

	.password-toggle {
		position: absolute;
		right: 12px;
		top: 50%;
		transform: translateY(-50%);
		background: none;
		border: none;
		color: #888;
		cursor: pointer;
		padding: 4px;
		border-radius: 4px;
		display: flex;
		align-items: center;
		justify-content: center;
		transition: color 0.2s;
	}

	.password-toggle:hover {
		color: #4a6cf7;
	}

	.password-toggle:focus {
		outline: none;
		color: #4a6cf7;
	}

	.password-input-container .form-input {
		padding-right: 45px;
	}

	.form-textarea {
		resize: vertical;
		min-height: 60px;
		font-family: inherit;
	}

	.company-section {
		background: rgba(74, 108, 247, 0.1);
		border: 1px solid rgba(74, 108, 247, 0.3);
		border-radius: 8px;
		padding: 16px;
		margin: 8px 0;
	}

	.field-error {
		color: #ff4444;
		font-size: 12px;
		margin-top: 4px;
	}

	.field-error p {
		margin: 0;
		padding: 2px 0;
	}

	.error-message {
		color: #ff4444;
		font-size: 14px;
		text-align: center;
		padding: 8px;
		background: rgba(255, 68, 68, 0.1);
		border-radius: 4px;
		margin: 8px 0;
	}

	.register-button {
		width: 100%;
		padding: 14px;
		background: #4a6cf7;
		border: none;
		border-radius: 8px;
		color: #ffffff;
		font-size: 16px;
		font-weight: 600;
		cursor: pointer;
		transition: background-color 0.2s;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
		margin-top: 8px;
	}

	.register-button:hover:not(:disabled) {
		background: #3a5ae7;
	}

	.register-button:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}

	.spinner {
		width: 16px;
		height: 16px;
		border: 2px solid transparent;
		border-top: 2px solid #ffffff;
		border-radius: 50%;
		animation: spin 1s linear infinite;
	}

	@keyframes spin {
		0% {
			transform: rotate(0deg);
		}
		100% {
			transform: rotate(360deg);
		}
	}

	.login-link {
		text-align: center;
		color: #888;
		font-size: 14px;
		margin-top: 8px;
	}

	.link-button {
		background: none;
		border: none;
		color: #4a6cf7;
		cursor: pointer;
		font-size: 14px;
		text-decoration: underline;
	}

	.link-button:hover {
		color: #3a5ae7;
	}

	/* Mobile responsive */
	@media (max-width: 768px) {
		.modal-container {
			margin: 10px;
			max-width: calc(100% - 20px);
		}

		.modal-content {
			padding: 0 20px 20px 20px;
		}

		.modal-title {
			font-size: 20px;
		}

		.customer-type-group {
			flex-direction: column;
		}
	}
</style>
